import { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import EmptyState from '../ui/EmptyState';

/**
 * Resultados D28D/sede a partir de GET /d28d/progress/me.
 * KPIs consumidor: Asistidos (Zoom), Días del plan, Inscritos.
 * compact = resumen; full = tablero (Mi Progreso).
 * hidePremiacion / useClassEnrollment se conservan por compatibilidad de props.
 */
export default function D28dProgressDashboard({
  compact = false,
  hidePremiacion = false, // eslint-disable-line no-unused-vars
  useClassEnrollment = false, // eslint-disable-line no-unused-vars
  refreshKey = 0,
  showWeightSnapshot = false,
}) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [weightHistory, setWeightHistory] = useState([]);

  useEffect(() => {
    let active = true;
    const load = () => {
      setError('');
      api.get('/d28d/progress/me')
        .then((r) => { if (active) setData(r.data?.data || null); })
        .catch(() => {
          if (!active) return;
          setData(null);
          setError(t('d28d.progress.load_error', 'No se pudo cargar tu progreso del programa.'));
        });
    };
    load();
    const onRefresh = () => load();
    window.addEventListener('d28d:progress:refresh', onRefresh);
    return () => {
      active = false;
      window.removeEventListener('d28d:progress:refresh', onRefresh);
    };
  }, [t, refreshKey]);

  useEffect(() => {
    if (!showWeightSnapshot || compact) {
      setWeightHistory([]);
      return undefined;
    }
    let active = true;
    api.get('/training/measurements/me')
      .then((r) => {
        if (!active) return;
        const rows = Array.isArray(r.data?.data) ? r.data.data : [];
        setWeightHistory(rows);
      })
      .catch(() => { if (active) setWeightHistory([]); });
    return () => { active = false; };
  }, [showWeightSnapshot, compact, refreshKey]);

  const weightChart = useMemo(
    () => [...weightHistory]
      .reverse()
      .filter((h) => h.weight_kg != null && Number.isFinite(Number(h.weight_kg)))
      .map((h) => ({
        fecha: String(h.recorded_at || '').slice(0, 10),
        peso: Number(h.weight_kg),
      })),
    [weightHistory],
  );

  const lastWeight = weightChart.length ? weightChart[weightChart.length - 1].peso : null;
  const prevWeight = weightChart.length > 1 ? weightChart[weightChart.length - 2].peso : null;
  const weightDelta = lastWeight != null && prevWeight != null
    ? Number((lastWeight - prevWeight).toFixed(1))
    : null;

  if (!data && !error) {
    return (
      <div className="card p-4 animate-pulse space-y-3">
        <div className="h-4 bg-white/10 rounded w-1/3" />
        <div className="h-2 bg-white/10 rounded w-full" />
        <div className="h-2 bg-white/10 rounded w-2/3" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        title={t('d28d.progress.unavailable', 'Progreso no disponible')}
        message={error}
      />
    );
  }

  const enrolledCount = data.classes_enrolled ?? 0;
  const attendedCount = data.classes_attended ?? 0;
  const planDays = data.plan_days ?? data.active_days ?? 0;

  if (compact) {
    return (
      <div className="card p-4">
        <div className="grid gap-3 text-sm grid-cols-3">
          <div>
            <p className="text-xs d28d-text-muted uppercase">{t('d28d.progress.attended', 'Asistidos')}</p>
            <p className="font-bold text-[var(--d28d-text)] text-lg">{attendedCount}</p>
          </div>
          <div>
            <p className="text-xs d28d-text-muted uppercase">{t('d28d.progress.plan_days', 'Días del plan')}</p>
            <p className="font-bold text-[var(--d28d-text)] text-lg">{planDays}</p>
          </div>
          <div>
            <p className="text-xs d28d-text-muted uppercase">{t('d28d.progress.enrolled', 'Inscritos')}</p>
            <p className="font-bold text-[var(--d28d-text)] text-lg">{enrolledCount}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">
              {t('d28d.progress.results_title', 'Resultados del ciclo')}
            </h3>
            {data.cycle_name ? (
              <p className="text-xs d28d-text-muted mt-1">{data.cycle_name}</p>
            ) : null}
          </div>
          {data.cycle_day ? (
            <p className="text-sm font-semibold text-[var(--d28d-text)]">
              {t('d28d.cycle.day_of', 'Día {day} de {total}', {
                day: data.cycle_day,
                total: data.cycle_total_days || 28,
              })}
            </p>
          ) : null}
        </div>
      </div>

      <div className="ef-stat-grid">
        <div className="ef-stat-card">
          <span className="ef-stat-card__label">{t('d28d.progress.attended', 'Asistidos')}</span>
          <span className="ef-stat-card__value ef-stat-card__value--metric">{attendedCount}</span>
        </div>
        <div className="ef-stat-card">
          <span className="ef-stat-card__label">{t('d28d.progress.plan_days', 'Días del plan')}</span>
          <span className="ef-stat-card__value ef-stat-card__value--metric">{planDays}</span>
        </div>
        <div className="ef-stat-card">
          <span className="ef-stat-card__label">{t('d28d.progress.enrolled', 'Inscritos')}</span>
          <span className="ef-stat-card__value ef-stat-card__value--metric">{enrolledCount}</span>
        </div>
        {showWeightSnapshot && lastWeight != null ? (
          <div className="ef-stat-card">
            <span className="ef-stat-card__label">{t('d28d.progress.last_weight', 'Último peso')}</span>
            <span className="ef-stat-card__value ef-stat-card__value--metric">
              {lastWeight} kg
              {weightDelta != null ? (
                <span className="text-xs font-medium d28d-text-muted ml-1">
                  ({weightDelta > 0 ? '+' : ''}{weightDelta})
                </span>
              ) : null}
            </span>
          </div>
        ) : null}
      </div>

      {showWeightSnapshot && weightChart.length > 1 ? (
        <div className="card p-4 space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wide d28d-text-muted">
            {t('d28d.progress.weight_trend', 'Tendencia de peso')}
          </h4>
          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="fecha" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 10 }} />
                <YAxis domain={['auto', 'auto']} tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }} width={36} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--d28d-surface-2, #1a1a1a)',
                    border: '1px solid var(--d28d-border)',
                    borderRadius: 8,
                    color: 'var(--d28d-text)',
                  }}
                />
                <Line type="monotone" dataKey="peso" stroke="var(--shell-primary, var(--d28d-yellow, #ffd700))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
